import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  useToast,
  useColorModeValue,
  Checkbox,
  CheckboxGroup,
  Stack,
  FormErrorMessage
} from '@chakra-ui/react';
import apiClient from '../lib/api/apiClient';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  leagueId: number;
  teamName?: string;
  onSuccess?: () => void;
}

const AVAILABLE_ROLES = [
  'Co-Owner',
  'General Manager',
  'Manager',
  'Hitting Coach',
  'Pitching Coach',
  'Scout',
  'Fan/Shareholder'
];

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  teamId,
  leagueId,
  teamName,
  onSuccess
}) => {
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    if (selectedRoles.length === 0) {
      newErrors.roles = 'Please select at least one role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      await apiClient.post('/team/invite-user', {
        team_id: teamId,
        league_id: leagueId,
        email_address: emailAddress,
        roles: selectedRoles,
        message: customMessage || undefined
      });

      toast({
        title: 'Invitation Sent',
        description: `An invitation has been sent to ${emailAddress}.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Reset form and close modal
      resetForm();
      onClose();
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast({
        title: 'Invitation Failed',
        description: error?.response?.data?.message || 'Failed to send invitation. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setEmailAddress('');
    setSelectedRoles([]);
    setCustomMessage('');
    setErrors({});
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg={bgColor} maxH="90vh" overflowY="auto">
        <ModalHeader color={textColor}>
          Invite User to Team
        </ModalHeader>
        <ModalCloseButton color={textColor} />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="md" color={textColor} mb={2}>
              Invite a user to join <strong>{teamName || 'your team'}</strong>. They will receive an email invitation with instructions on how to accept.
            </Text>

            <FormControl isRequired isInvalid={!!errors.emailAddress}>
              <FormLabel color={textColor}>Email Address</FormLabel>
              <Input
                type="email"
                placeholder="user@example.com"
                value={emailAddress}
                onChange={(e) => {
                  setEmailAddress(e.target.value);
                  if (errors.emailAddress) setErrors({ ...errors, emailAddress: '' });
                }}
                color={textColor}
                borderColor={borderColor}
                _hover={{ borderColor: useColorModeValue('gray.300', 'gray.600') }}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px #38a169' }}
              />
              <FormErrorMessage>{errors.emailAddress}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.roles}>
              <FormLabel color={textColor}>Team Role(s)</FormLabel>
              <CheckboxGroup 
                value={selectedRoles} 
                onChange={(values) => {
                  setSelectedRoles(values as string[]);
                  if (errors.roles) setErrors({ ...errors, roles: '' });
                }}
              >
                <Stack spacing={2} pl={2}>
                  {AVAILABLE_ROLES.map((role) => (
                    <Checkbox 
                      key={role} 
                      value={role}
                      colorScheme="green"
                      color={textColor}
                    >
                      {role}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
              <FormErrorMessage>{errors.roles}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel color={textColor}>Message (Optional)</FormLabel>
              <Textarea
                placeholder="Add a personal message to include in the invitation..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={4}
                resize="vertical"
                color={textColor}
                borderColor={borderColor}
                _hover={{ borderColor: useColorModeValue('gray.300', 'gray.600') }}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px #38a169' }}
                maxLength={300}
              />
              <Text 
                fontSize="xs" 
                color={useColorModeValue('gray.500', 'gray.400')} 
                mt={1}
                textAlign="right"
              >
                {customMessage.length}/300 characters
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Sending..."
          >
            Send Invitation
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default InviteUserModal;

